/*
 * The Marketing suite's rules (draft 12, FEATURE-MAP 12), ported from the WordPress Steward's outreach.php,
 * outreach-ai.php, finder.php, finder-job.php, maprank.php, webrank.php, pageopt.php, gbp.php, posts.php, channels.php
 * and the owner's rules. Small pure functions so the server, the screens and the preview agree.
 */
import { addDays } from "./schedule.js";

// Channels ------------------------------------------------------------------------------------------------------------

/** How clients find us, in the order the old channels page showed them (channels.php and 15-marketing.js). */
export const CHANNEL_WORDS = {
  lsa: "Google Local Services Ads",
  gads: "Google Ads",
  gbp: "Google Maps profile",
  search: "Organic Google and website",
  facebook: "Facebook and Instagram",
  nextdoor: "Nextdoor",
  yelp: "Yelp",
  thumbtack: "Thumbtack",
  angi: "Angi",
  referral: "Referrals and word of mouth",
  pastclient: "Past client texts",
  email: "Cold email",
  doorhanger: "Door hangers",
  flyer: "Flyers and mailers",
  yard: "Yard signs",
  truck: "Truck or van",
  phone: "Called or texted",
  other: "Other",
} as const;
export type Channel = keyof typeof CHANNEL_WORDS;
export const CHANNELS = Object.keys(CHANNEL_WORDS) as Channel[];

/** Puts a lead source, a website touch channel or a typed cost channel into one channel. Unknown words are Other. */
export function channelGroup(name: string | null | undefined): Channel {
  const n = (name ?? "")
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, "_");
  if (!n) return "other";
  if ((CHANNELS as string[]).includes(n)) return n as Channel;
  if (/^(lsa|local_services|google_local)/.test(n)) return "lsa";
  if (/google_ads|^gads|adwords|ppc/.test(n)) return "gads";
  if (/google_business|gbp|gmb|maps/.test(n)) return "gbp";
  if (/^(google|bing|organic|website|seo)/.test(n)) return "search";
  if (/facebook|instagram|meta|^fb|^ig/.test(n)) return "facebook";
  if (/cold_email|outreach|^email/.test(n)) return "email";
  if (/repeat|past_client|pastclient/.test(n)) return "pastclient";
  if (n.includes("yard")) return "yard";
  if (n.includes("door")) return "doorhanger";
  if (/flyer|mailer/.test(n)) return "flyer";
  if (/truck|van/.test(n)) return "truck";
  if (/referr|word_of_mouth|friend/.test(n)) return "referral";
  if (/call|text|phone/.test(n)) return "phone";
  for (const c of ["nextdoor", "yelp", "thumbtack", "angi"] as const) if (n.includes(c)) return c;
  return "other";
}

/** ROI the old way: money back minus spend, and how many times the spend came back. No spend means no "times back". */
export function channelReturn(revenueCents: number, spendCents: number): { netCents: number; timesBack: number | null } {
  return {
    netCents: revenueCents - spendCents,
    timesBack: spendCents > 0 ? Math.round((revenueCents / spendCents) * 10) / 10 : null,
  };
}

// Cold email ---------------------------------------------------------------------------------------------------------

/**
 * Our main domains. Cold email never goes from these (owner rule): only from a separate outreach domain in its own
 * Google Workspace, so a spam complaint can't hurt the brands' real email.
 */
export const MAIN_DOMAINS = [
  "thecleanersusa.com",
  "thegroundsmen.com",
  "exteriorwashers.com",
  "thetradesbrands.com",
  "themarketers.agency",
];

const domainOf = (email: string) => email.trim().toLowerCase().split("@")[1] ?? "";

/** Why this from address can't send cold email, or null when it can. moreMain adds a company's own website domains. */
export function outreachFromProblem(fromEmail: string, moreMain: readonly string[] = []): string | null {
  const d = domainOf(fromEmail);
  if (!/^[^@\s]+@[a-z0-9.-]+\.[a-z]{2,}$/i.test(fromEmail.trim())) return "Type a full email address.";
  if (FREE_MAIL.test(`@${d}`)) return "Use an address on your own outreach domain, not a free email.";
  const main = [...MAIN_DOMAINS, ...moreMain.map((m) => m.toLowerCase().replace(/^www\./, ""))];
  if (main.some((m) => m && (d === m || d.endsWith(`.${m}`))))
    return "Cold email never goes from a main brand domain. Use the separate outreach domain.";
  return null;
}

/** Sending hours, Monday to Friday 8:30 AM to 4:30 PM in the company's time (outreach.php). */
export const OUTREACH_WINDOW = { start: "08:30", end: "16:30", days: [1, 2, 3, 4, 5] } as const;
export const OUTREACH_MAX_STEPS = 8;
export const OUTREACH_MAX_CAP = 100;

/** Is it sending time? time is HH:MM, weekday 1 (Monday) to 7 (Sunday). */
export function inSendWindow(time: string, weekday: number): boolean {
  return (
    (OUTREACH_WINDOW.days as readonly number[]).includes(weekday) && time >= OUTREACH_WINDOW.start && time < OUTREACH_WINDOW.end
  );
}

/** Warm-up: a new inbox starts at 10 a day and adds 5 for each day it has sent on, up to its cap. */
export function warmupCap(cap: number, ramp: boolean, sendDaysBefore: number): number {
  return ramp ? Math.min(cap, 10 + 5 * Math.max(0, sendDaysBefore)) : cap;
}

/**
 * Fills {{key|fallback}} tags. A tag with no value and no fallback is missing; the prospect waits instead of getting a
 * broken email.
 */
export function fillTags(text: string, vars: Record<string, string | null | undefined>): { text: string; missing: string[] } {
  const missing: string[] = [];
  const out = text.replace(/\{\{\s*([a-z_]+)\s*(?:\|([^}]*))?\}\}/gi, (_m, key: string, fallback: string | undefined) => {
    const v = vars[key.toLowerCase()]?.trim();
    if (v) return v;
    if (fallback !== undefined) return fallback.trim();
    missing.push(key.toLowerCase());
    return "";
  });
  return { text: out, missing };
}

/** The footer on every cold email: the mailing address and how to stop them. */
export function coldFooter(address: string): string {
  return `\n\n--\n${address}\nIf you'd rather not hear from me, reply "stop" and I won't email again.`;
}

const STOP_WORDS_RE =
  /\b(unsubscribe|remove me|take me off|stop emailing|do not (contact|email)|don't (contact|email)|not interested)\b|^\s*stop\b/i;

/** What a reply to a cold email is (outreach.php's order): a bounce, an auto reply, a stop, a delay, or a real reply. */
export function replyKind(r: {
  from: string;
  subject: string;
  body: string;
  auto?: boolean;
}): "bounce" | "auto" | "stop" | "delay" | "reply" {
  const subject = r.subject.toLowerCase();
  if (
    /(delay)|delayed|will retry|deferred|temporar/i.test(`${subject} ${r.body}`) &&
    !/permanent|failed permanently/i.test(r.body)
  )
    return "delay";
  if (/mailer-daemon|postmaster/i.test(r.from) || /delivery status notification|undeliver|address not found/.test(subject))
    return "bounce";
  if (r.auto || /out of (the )?office|automatic reply|auto.?reply|on vacation/.test(subject)) return "auto";
  if (STOP_WORDS_RE.test(r.body) || subject.trim() === "unsubscribe") return "stop";
  return "reply";
}

/** Free email providers (outreach.php STEWARD_OUTREACH_FREE). */
export const FREE_MAIL = /@(gmail|yahoo|hotmail|outlook|aol|icloud|live|msn|me|comcast|att|bellsouth|cox)\./i;

const AI_BAN = [
  "amazing",
  "awesome",
  "stunning",
  "beautiful",
  "gorgeous",
  "curated",
  "impressive",
  "impressed",
  "incredible",
  "wonderful",
  "fantastic",
  "unique",
  "vibrant",
  "nestled",
  "passion",
  "passionate",
  "elevate",
  "elevates",
  "elevated",
  "solution",
  "solutions",
  "journey",
  "delight",
  "delightful",
  "exceptional",
  "top-notch",
  "world-class",
  "love your",
  "came across",
  "stumbled",
  "i noticed",
  "i saw",
  "i see",
  "hope this",
  "reaching out",
  "we",
  "our",
  "us",
  "free",
  "guarantee",
  "guaranteed",
  "cheap",
  "discount",
];

/**
 * Checks an AI-written first line (outreach-ai.php): 8 to 18 words, a plain fact about them, no praise, never about us,
 * no prices or offers, no dashes, colons or "!", and any number in it must be on their site. Answers what is wrong.
 */
export function aiLineProblems(line: string, siteText: string): string[] {
  const out: string[] = [];
  const words = line.trim().split(/\s+/).filter(Boolean);
  if (words.length < 8 || words.length > 18) out.push("Use 8 to 18 words.");
  const low = ` ${line.toLowerCase().replace(/[^a-z0-9' -]/g, " ")} `;
  const hit = AI_BAN.find((w) => low.includes(` ${w} `));
  if (hit) out.push(`Leave out "${hit}".`);
  if (/[!:;–—]| - /.test(line)) out.push("No dashes, colons or exclamation marks.");
  if (/^i\b/i.test(line.trim())) out.push("Don't start with I.");
  if (/https?:|www\.|@/.test(line)) out.push("No links or emails.");
  for (const n of line.match(/\d[\d,.]*/g) ?? []) if (!siteText.includes(n)) out.push(`The number ${n} is not on their site.`);
  return out;
}

// Lead finder --------------------------------------------------------------------------------------------------------

/** A Google page is paid for only when it has at least this many businesses we have not seen (finder-job.php). */
export const FINDER_MIN_NEW = 10;
export const FINDER_BIG_MAX = 2000;
export const FINDER_SMALL_MAX = 60;
/** More reviews than this is likely a chain. */
export const FINDER_MAX_REVIEWS = 400;
const SOCIAL_HOSTS =
  /(^|\.)(facebook|instagram|linktr|yelp|tiktok)\.|(^|\.)(square\.site|business\.site|wixsite\.com|godaddysites\.com)$/;
const JUNK_EMAIL =
  /(no-?reply|privacy|abuse|postmaster|webmaster|hostmaster|wordpress|admin)@|@(.*\.)?(wix|squarespace|godaddy|shopify|sentry|example)\.|\.(png|jpe?g|gif|svg|webp|css|js)$/i;

/** Pay for this Google page only when it brings enough new businesses. */
export const finderPagePays = (newOnPage: number): boolean => newOnPage >= FINDER_MIN_NEW;

/** Big search, as the old Steward's (finder-job.php, owner answer 114): Google searches a day, at most. */
export const FINDER_DAY_CAP = 150;
/**
 * The spare list (owner answer 122): businesses on a page not paid for wait here and are looked up one at a time inside
 * Google's free monthly share of single lookups (1,000 for the whole Google account; this leaves room for map rank and a
 * margin, as the old Steward did). Counted across every company, since they share HQ's one Google key.
 */
export const FINDER_FREE_LOOKUPS = 700;
/** The most businesses one search keeps waiting. */
export const FINDER_SPARE_MAX = 3000;
/** Free lookups in one turn. */
export const FINDER_SPARE_TURN = 10;

/** Adds new place ids to the end of a spare list, once each, keeping the newest FINDER_SPARE_MAX. */
export function addSpare(spare: readonly string[], ids: readonly string[]): string[] {
  const out = [...spare];
  const have = new Set(spare);
  for (const id of ids) {
    if (have.has(id)) continue;
    have.add(id);
    out.push(id);
  }
  return out.slice(-FINDER_SPARE_MAX);
}
/** A map box: [south, west, north, east]. */
export type MapBox = [number, number, number, number];
/** One search waiting in a Big search: a page of the area or of one part of its box. */
export interface FinderItem {
  token: string | null;
  box: MapBox | null;
  /** Pages read so far for this box (0 is the first). */
  n: number;
  depth: number;
}

/** A box in 4: south-west, south-east, north-west, north-east. */
export function splitBox(b: MapBox): MapBox[] {
  const lat = Math.round(((b[0] + b[2]) / 2) * 1e6) / 1e6;
  const lng = Math.round(((b[1] + b[3]) / 2) * 1e6) / 1e6;
  return [
    [b[0], b[1], lat, lng],
    [b[0], lng, lat, b[3]],
    [lat, b[1], b[2], lng],
    [lat, lng, b[2], b[3]],
  ];
}

/**
 * What comes after one page of a Big search: its next page; or, when the third page was full (more than Google's 60 in
 * the box), the box again in 4 parts, 3 levels deep at most.
 */
export function finderNext(item: FinderItem, idsOnPage: number, next: string | null): FinderItem[] {
  if (next) return [{ ...item, token: next, n: item.n + 1 }];
  if (item.n >= 2 && idsOnPage >= 20 && item.box && item.depth < 3)
    return splitBox(item.box).map((box) => ({ token: null, box, n: 0, depth: item.depth + 1 }));
  return [];
}

export function hostOf(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(/^https?:/i.test(url) ? url : `https://${url}`).hostname.toLowerCase().replace(/^www\./, "");
  } catch {
    return null;
  }
}

/** Why a place is skipped before its site is read, or null to read it. */
export function finderSkip(p: { website: string | null; reviews: number; closed: boolean }): string | null {
  if (p.closed) return "Closed for good";
  const host = hostOf(p.website);
  if (!host) return "No website";
  if (SOCIAL_HOSTS.test(host)) return "Only a social page";
  if (p.reviews > FINDER_MAX_REVIEWS) return "Likely a chain";
  return null;
}

/** Emails on a page: mailto links, plain text, "name [at] site [dot] com", and Cloudflare's hidden ones. */
export function extractEmails(html: string): string[] {
  const found = new Set<string>();
  const text = html
    .replace(/\s*[[(]\s*at\s*[\])]\s*/gi, "@")
    .replace(/\s*[[(]\s*dot\s*[\])]\s*/gi, ".")
    .replace(/data-cfemail="([0-9a-f]+)"/gi, (_m, hex: string) => {
      const key = parseInt(hex.slice(0, 2), 16);
      let s = "";
      for (let i = 2; i < hex.length; i += 2) s += String.fromCharCode(parseInt(hex.slice(i, i + 2), 16) ^ key);
      return ` ${s} `;
    });
  for (const m of text.match(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi) ?? []) {
    const e = m.toLowerCase().replace(/^\d{3,}[-.]?/, "");
    if (!JUNK_EMAIL.test(e)) found.add(e);
  }
  return [...found];
}

const GENERIC_BOX = /^(info|hello|contact|office|support|sales|service|admin|team|mail|inquiries|help)@/;

/** How good an email is for a site: its own domain 20, a free email 10, plus 5 when it is a person. Under 10 is dropped. */
export function emailScore(email: string, siteHost: string): number {
  const d = domainOf(email);
  const host = siteHost.replace(/^www\./, "");
  const name = (h: string) => h.split(".").slice(-2, -1)[0] ?? h;
  const own =
    d === host ||
    d.endsWith(`.${host}`) ||
    host.endsWith(`.${d}`) ||
    name(d) === name(host) ||
    (name(d).length >= 5 && name(host).length >= 5 && (name(d).includes(name(host)) || name(host).includes(name(d))));
  const base = own ? 20 : FREE_MAIL.test(email) ? 10 : 0;
  return base === 0 ? 0 : base + (GENERIC_BOX.test(email) ? 0 : 5);
}

/** The best email for a site, or null. */
export function bestEmail(emails: readonly string[], siteHost: string): string | null {
  const scored = emails.map((e) => ({ e, s: emailScore(e, siteHost) })).filter((x) => x.s >= 10);
  scored.sort((a, b) => b.s - a.s);
  return scored[0]?.e ?? null;
}

// Map rank grid ------------------------------------------------------------------------------------------------------

export const GRID_SIZES = [3, 5, 7, 9, 11, 13] as const;
/** A place not in the top 20 counts as 21 in the average. */
export const GRID_MISS = 21;

/** The grid's points, row by row from the north, miles apart (maprank.php). */
export function gridPoints(lat: number, lng: number, size: number, miles: number): { lat: number; lng: number }[] {
  const half = (size - 1) / 2;
  const dLat = miles / 69;
  const dLng = miles / (69.172 * Math.cos((lat * Math.PI) / 180));
  const out: { lat: number; lng: number }[] = [];
  for (let r = 0; r < size; r++)
    for (let c = 0; c < size; c++)
      out.push({
        lat: Math.round((lat + (half - r) * dLat) * 1e6) / 1e6,
        lng: Math.round((lng + (c - half) * dLng) * 1e6) / 1e6,
      });
  return out;
}

/** How far out the spots reach, in miles from the middle (maprank.php: "like new orleans has a 15 mile radius"). */
export const GRID_AREA_MILES = [3, 5, 10, 15, 20, 25, 30] as const;
/** How close the spots are, as spots across the widest row. */
export const GRID_DENSITY = [
  { size: 7, label: "Fewer spots" },
  { size: 9, label: "Close" },
  { size: 11, label: "Closer" },
  { size: 13, label: "Closest" },
] as const;

/** The miles between spots so the widest row reaches the edge of the area. */
export function gridMilesApart(radius: number, size: number): number {
  return Math.round((radius / Math.max(1, (size - 1) / 2)) * 100) / 100;
}

/**
 * The spots a grid checks: every point of the square inside the area's circle (radius 0 keeps the whole square), less the
 * ones the owner turned off (water, no homes). `i` is the point's place in the full square, row by row from the north.
 */
export function gridSpots(
  lat: number,
  lng: number,
  size: number,
  miles: number,
  radius = 0,
  skip: readonly number[] = [],
): { i: number; lat: number; lng: number }[] {
  const half = (size - 1) / 2;
  const off = new Set(skip);
  return gridPoints(lat, lng, size, miles).flatMap((p, i) => {
    const r = Math.floor(i / size);
    const c = i % size;
    if (radius > 0 && miles * Math.hypot(r - half, c - half) > radius + 0.001) return [];
    if (off.has(i)) return [];
    return [{ i, ...p }];
  });
}

/**
 * Two runs of the same spots, older then newer: where we moved up, down or stayed (a miss counts as 21). Null when the
 * spots are not the same (the area changed between them), so only the totals compare.
 */
export function gridChange(
  before: { spots: readonly (readonly [number, number])[]; points: readonly (number | null)[] },
  after: { spots: readonly (readonly [number, number])[]; points: readonly (number | null)[] },
): { better: number; worse: number; same: number } | null {
  if (before.points.length !== after.points.length || JSON.stringify(before.spots) !== JSON.stringify(after.spots)) return null;
  const val = (r: number | null) => (r === null || r < 1 ? GRID_MISS : Math.min(r, GRID_MISS));
  let better = 0;
  let worse = 0;
  before.points.forEach((b, i) => {
    const a = val(after.points[i] ?? null);
    if (a < val(b)) better += 1;
    else if (a > val(b)) worse += 1;
  });
  return { better, worse, same: before.points.length - better - worse };
}

/** Miles between two map spots (flat, close enough across a city). */
export function milesBetween(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const dLat = (a.lat - b.lat) * 69;
  const dLng = (a.lng - b.lng) * 69.172 * Math.cos((((a.lat + b.lat) / 2) * Math.PI) / 180);
  return Math.hypot(dLat, dLng);
}

/** One listing's circle: its middle, spots across, miles out and the spots turned off. */
export interface RankCircleShape {
  lat: number;
  lng: number;
  size: number;
  radius: number;
  skip: readonly number[];
  /** Miles between spots for a square with no area (radius 0); an area sets its own. */
  miles?: number;
}

/**
 * The spots for several listings' circles (owner, 2026-11-02: "since i have 2, 2 different searches or circles"): each
 * circle's spots, less any closer than half a spacing to a spot already kept, so where the circles overlap a spot is
 * searched once. One search answers for every listing.
 */
export function circleSpots(circles: readonly RankCircleShape[]): { lat: number; lng: number }[] {
  const out: { lat: number; lng: number; gap: number }[] = [];
  for (const c of circles) {
    const miles = c.radius > 0 ? gridMilesApart(c.radius, c.size) : (c.miles ?? 1);
    for (const p of gridSpots(c.lat, c.lng, c.size, miles, c.radius, c.skip))
      if (!out.some((q) => milesBetween(p, q) < Math.min(miles, q.gap) / 2)) out.push({ lat: p.lat, lng: p.lng, gap: miles });
  }
  return out.map(({ lat, lng }) => ({ lat, lng }));
}

/**
 * One run seen for one listing (k) or for all of them (null, the best of them at each spot). A listing sees the spots
 * inside its own circle with its own place there. Runs from before listings were kept apart have one view only.
 */
export function rankView(
  run: {
    spots: readonly (readonly [number, number])[];
    points: readonly (number | null)[];
    each: readonly (readonly (number | null)[])[];
    circles: readonly { lat: number; lng: number; radius: number }[];
  },
  k: number | null,
): { idx: number[]; points: (number | null)[]; summary: { average: number; top3: number; found: number } } {
  const c = k === null ? undefined : run.circles[k];
  const idx =
    c === undefined
      ? run.points.map((_p, i) => i)
      : run.spots.flatMap(([lat, lng], i) => (milesBetween({ lat, lng }, c) <= c.radius + 0.25 ? [i] : []));
  const points = idx.map((i) => (k === null || c === undefined ? (run.points[i] ?? null) : (run.each[i]?.[k] ?? null)));
  return { idx, points, summary: gridSummary(points) };
}

/** The searches each trade starts with (maprank.php steward_maprank_default_keywords). */
export function mapRankKeywords(trade: string): string[] {
  if (trade === "lawn") return ["lawn care", "lawn mowing", "landscaping", "yard cleanup"];
  if (trade === "washers") return ["pressure washing", "window cleaning", "soft washing", "house washing"];
  if (trade === "agency") return ["marketing agency", "google ads agency", "facebook ads agency", "local seo"];
  return ["house cleaning", "maid service", "deep cleaning", "move out cleaning", "commercial cleaning"];
}

/** The average place (a miss counts as 21, one decimal), how many points were top 3, and how many were top 20. */
export function gridSummary(ranks: readonly (number | null)[]): { average: number; top3: number; found: number } {
  if (ranks.length === 0) return { average: GRID_MISS, top3: 0, found: 0 };
  const vals = ranks.map((r) => (r === null || r < 1 ? GRID_MISS : Math.min(r, GRID_MISS)));
  const average = Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10;
  return {
    average,
    top3: ranks.filter((r) => r !== null && r >= 1 && r <= 3).length,
    found: ranks.filter((r) => r !== null && r >= 1 && r <= 20).length,
  };
}

/** How many of the businesses ahead of us get their rating looked up after a run (one Place Details call each). */
export const RIVALS_LOOKED_UP = 5;

/** One business in the Maps results at a point. */
export interface RankHit {
  name: string;
  placeId: string | null;
}

/** A business that came in ahead of us: at how many points, its best place, and its average over the grid. */
export interface RivalAhead {
  name: string;
  placeId: string | null;
  ahead: number;
  best: number;
  average: number;
}

/** One of our Google listings, as Maps results are matched against it. */
export interface RankUs {
  name: string;
  placeId: string | null;
}

/**
 * A Maps result is ours: our Google place id when we know it, else our name (any case). With more than one listing,
 * any of them.
 */
export function isUs(h: RankHit, ours: RankUs | readonly RankUs[]): boolean {
  const all: readonly RankUs[] = Array.isArray(ours) ? ours : [ours as RankUs];
  return all.some((o) => (!!o.placeId && h.placeId === o.placeId) || h.name.trim().toLowerCase() === o.name.trim().toLowerCase());
}

/**
 * The businesses ahead of us in one run (maprank-comp.php): at each point, every business above our place (all 20 when
 * we are not in the top 20). Ours never count. Most points ahead first, then the best place, then the best average.
 */
export function rivalsAhead(points: readonly (readonly RankHit[])[], ours: RankUs | readonly RankUs[]): RivalAhead[] {
  const keyOf = (h: RankHit) => h.placeId ?? `name:${h.name.trim().toLowerCase()}`;
  const seen = new Map<string, { name: string; placeId: string | null; ahead: number; best: number; at: (number | null)[] }>();
  points.forEach((hits, p) => {
    const mine = hits.findIndex((h) => isUs(h, ours));
    const cut = mine < 0 ? hits.length : mine;
    hits.forEach((h, i) => {
      if (isUs(h, ours)) return;
      const k = keyOf(h);
      const r = seen.get(k) ?? {
        name: h.name,
        placeId: h.placeId,
        ahead: 0,
        best: 99,
        at: Array<number | null>(points.length).fill(null),
      };
      if (r.at[p] === null) r.at[p] = i + 1;
      if (i < cut) {
        r.ahead += 1;
        r.best = Math.min(r.best, i + 1);
      }
      seen.set(k, r);
    });
  });
  return [...seen.values()]
    .filter((r) => r.ahead > 0)
    .map((r) => ({ name: r.name, placeId: r.placeId, ahead: r.ahead, best: r.best, average: gridSummary(r.at).average }))
    .sort((a, b) => b.ahead - a.ahead || a.best - b.best || a.average - b.average);
}

/**
 * Plain words comparing our reviews and rating with the businesses ahead of us, or null when none of them was looked up.
 */
export function rivalCompare(
  us: { rating: number | null; reviews: number | null } | null,
  rivals: readonly { rating: number | null; reviews: number | null }[],
): string | null {
  const known = rivals.filter((r): r is { rating: number | null; reviews: number } => r.reviews !== null);
  if (known.length === 0) return null;
  if (us?.reviews == null) return "Your own reviews show here once your Google profile is read.";
  const mine = us.reviews;
  const more = known.filter((r) => r.reviews > mine);
  const rated = known.map((r) => r.rating).filter((x): x is number => x !== null);
  const avg = rated.length ? Math.round((rated.reduce((a, b) => a + b, 0) / rated.length) * 10) / 10 : null;
  const stars =
    avg !== null && us.rating !== null
      ? ` Their ratings average ${avg.toFixed(1)} stars and yours is ${us.rating.toFixed(1)}.`
      : "";
  if (more.length === 0)
    return `You have more reviews than every business ahead of you.${stars} Ask for a few new ones each week to keep it that way.`;
  const most = Math.max(...more.map((r) => r.reviews));
  const who = more.length === 1 ? "has" : "have";
  return `${String(more.length)} of the ${String(known.length)} businesses ahead of you ${who} more reviews than you. The most is ${String(most)} and you have ${String(mine)}.${stars} A few new reviews each week helps you move up.`;
}

// Website rank -------------------------------------------------------------------------------------------------------

export const WEB_RANK_MAX = 25;
export const WEB_RANK_TOP = 20;

/** The two 28 day windows (webrank.php): Search Console lags 2 days, so the latest ends 3 days ago. */
export function webRankWindows(today: string): { current: { from: string; to: string }; prior: { from: string; to: string } } {
  return {
    current: { from: addDays(today, -30), to: addDays(today, -3) },
    prior: { from: addDays(today, -58), to: addDays(today, -31) },
  };
}

/** Places moved up (a positive number is better), or null when either window has no place. */
export function placeChange(current: number | null, prior: number | null): number | null {
  if (current === null || prior === null) return null;
  return Math.round((prior - current) * 10) / 10;
}

// Page optimizer -----------------------------------------------------------------------------------------------------

export interface BriefTerm {
  term: string;
  pages: number;
  lo: number;
  hi: number;
}
export interface PageBrief {
  words: number;
  terms: BriefTerm[];
  topics: string[];
  headings: string[];
  questions: string[];
  pages: string[];
}

const STOP = new Set(
  "a an and are as at be by for from has have how i in is it its of on or our that the their this to was we what when where which who why will with you your".split(
    " ",
  ),
);
const wordsOf = (s: string) => s.toLowerCase().match(/[a-z0-9']+/g) ?? [];
const countIn = (text: string, term: string) =>
  (` ${wordsOf(text).join(" ")} `.match(new RegExp(` ${wordsOf(term).join(" ")} `, "g")) ?? []).length;

/** A heading or question is covered when 60% of its words that matter are on the page. */
export function covers(text: string, phrase: string): boolean {
  const need = wordsOf(phrase).filter((w) => !STOP.has(w));
  if (need.length === 0) return true;
  const have = new Set(wordsOf(text));
  return need.filter((w) => have.has(w)).length / need.length >= 0.6;
}

/** Price words are dropped from briefs for our own brands (no prices on the websites). */
export const PRICE_TERMS = /\b(price|prices|pricing|cost|costs|rate|rates|fee|fees|\$)/i;

/**
 * Scores a page from 0 to 100 against a brief (pageopt.php): terms 45%, topics 15%, length 15%, headings 15%,
 * questions 10%. Each term is weighted by how many top pages use it; too few uses earn part, far too many earn 70%.
 */
export function pageScore(
  page: { text: string; headings: string[] },
  brief: PageBrief,
): { score: number; parts: { name: string; got: number; tip: string | null }[] } {
  const words = wordsOf(page.text).length;
  const terms = brief.terms.slice(0, 30);
  let tw = 0;
  let tg = 0;
  const missing: string[] = [];
  for (const t of terms) {
    const c = countIn(page.text, t.term);
    const got = c === 0 ? 0 : c < t.lo ? c / t.lo : c > Math.max(3, t.hi * 1.5) ? 0.7 : 1;
    if (c === 0) missing.push(t.term);
    tw += t.pages;
    tg += got * t.pages;
  }
  const all = `${page.text} ${page.headings.join(" ")}`;
  const share = (list: string[]) => (list.length === 0 ? 1 : list.filter((h) => covers(all, h)).length / list.length);
  const termsGot = tw === 0 ? 1 : tg / tw;
  const topicsGot = share(brief.topics);
  const wordsGot = brief.words > 0 ? Math.min(1, words / brief.words) : 1;
  const headingsGot = share(brief.headings);
  const questionsGot = share(brief.questions);
  const score = Math.max(
    0,
    Math.min(
      100,
      Math.round(100 * (0.45 * termsGot + 0.15 * topicsGot + 0.15 * wordsGot + 0.15 * headingsGot + 0.1 * questionsGot)),
    ),
  );
  const pct = (x: number) => Math.round(x * 100);
  return {
    score,
    parts: [
      {
        name: "Words the top pages use",
        got: pct(termsGot),
        tip: missing.length ? `Add ${missing.slice(0, 5).join(", ")}` : null,
      },
      { name: "Topics", got: pct(topicsGot), tip: topicsGot < 1 ? "Cover the topics the top pages cover" : null },
      {
        name: "Length",
        got: pct(wordsGot),
        tip: wordsGot < 1 ? `The top pages have about ${String(brief.words)} words. This page has ${String(words)}.` : null,
      },
      { name: "Headings", got: pct(headingsGot), tip: headingsGot < 1 ? "Add headings like the top pages have" : null },
      { name: "Questions answered", got: pct(questionsGot), tip: questionsGot < 1 ? "Answer the questions people ask" : null },
    ],
  };
}

// Google Business Profile and posts ----------------------------------------------------------------------------------

const PRICE_RE = /\$\s?\d|\b\d+\s?(dollars|bucks)\b/i;
const PHONE_RE = /\(?\b\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}\b/;
const URL_RE = /https?:\/\/|www\./i;

/**
 * Checks a profile description (owner rule): sales copy with the result, proof and a come-back promise up front, real
 * ratings only. Google allows 750 characters and shows about 250 before "More". No prices, links or phone numbers.
 */
export function gbpDescriptionProblems(text: string, live: { rating: number | null; reviewCount: number }): string[] {
  const out: string[] = [];
  const t = text.trim();
  if (t.length < 100) out.push("Write at least 100 characters. Put the result, the proof and the come-back promise first.");
  if (t.length > 750) out.push("Google allows 750 characters.");
  if (PRICE_RE.test(t)) out.push("No prices.");
  if (URL_RE.test(t)) out.push("No links. Google takes them out.");
  if (PHONE_RE.test(t)) out.push("No phone numbers. Google takes them out.");
  for (const m of t.matchAll(/(\d(?:\.\d)?)\s*(?:stars?|star rating|rating)/gi)) {
    const said = Number(m[1]);
    if (live.rating === null || Math.abs(said - live.rating) > 0.05)
      out.push(`The live rating is ${live.rating === null ? "not known" : live.rating.toFixed(1)}. Use the real rating.`);
  }
  for (const m of t.matchAll(/(\d[\d,]*)\+?\s*(?:reviews|five.star reviews)/gi)) {
    const said = Number(m[1]!.replace(/,/g, ""));
    if (said > live.reviewCount) out.push(`Google shows ${String(live.reviewCount)} reviews. Don't say more.`);
  }
  if (!/come back|re-?clean|redo|make it right|guarantee/i.test(t.slice(0, 250)))
    out.push("Put the come-back promise in the first 250 characters.");
  return out;
}

/** Checks a profile post: names the service and the area, at most 1,500 characters, no price or phone number. */
export function gbpPostProblems(p: { body: string; service: string; area: string }): string[] {
  const out: string[] = [];
  const low = p.body.toLowerCase();
  if (p.body.length > 1500) out.push("Google posts allow 1,500 characters.");
  if (!low.includes(p.service.toLowerCase().trim())) out.push("Name the service in the post.");
  if (!low.includes(p.area.toLowerCase().trim())) out.push("Name the area in the post.");
  if (PRICE_RE.test(p.body)) out.push("No prices.");
  if (PHONE_RE.test(p.body)) out.push("No phone numbers. The Book button does that.");
  return out;
}

/** Tags that keep a photo out of posts (owner rule: no toilets). */
export const MEDIA_BLOCKED_TAGS = ["toilet", "bathroom toilet", "stock"];

/**
 * Checks a Facebook and Instagram post (owner rule): a hook or question first, a short tip people save, real photos
 * (never toilets or stock), no hours filler, no prices. Instagram needs a photo. 2,200 characters at most.
 */
export function socialPostProblems(p: { body: string; platforms: string[]; photos: { tags: string[] }[] }): string[] {
  const out: string[] = [];
  const first = p.body.trim().split(/\n/)[0] ?? "";
  if (p.body.length > 2200) out.push("Posts allow 2,200 characters.");
  if (!first.endsWith("?") && first.length > 90) out.push("Open with a short hook or a question.");
  if (/\b(we are|we're) open\b|\bhours\b|\bopen (mon|tue|wed|thu|fri|sat|sun|daily|today)/i.test(p.body))
    out.push("Leave out the hours.");
  if (PRICE_RE.test(p.body)) out.push("No prices.");
  if (p.platforms.includes("instagram") && p.photos.length === 0) out.push("Instagram needs a photo.");
  if (p.photos.length === 0) out.push("Add a real photo from a job.");
  if (p.photos.some((ph) => ph.tags.some((t) => MEDIA_BLOCKED_TAGS.includes(t.toLowerCase()))))
    out.push("That photo can't go in a post. Pick another.");
  if (p.platforms.includes("google") && p.body.length > SOCIAL_LIMITS.google)
    out.push("Google takes up to 1,500 characters. Shorten it, or untick Google.");
  return out;
}

/** The most characters each place takes (posts.php). */
export const SOCIAL_LIMITS = { facebook: 2200, instagram: 2200, google: 1500 } as const;
/** About how much of a caption Instagram and Facebook show before "more", so the hook has to fit in it. */
export const SOCIAL_FOLD = 125;
/** Instagram allows 5 hashtags a post since December 2025. */
export const SOCIAL_MAX_TAGS = 5;

/**
 * First lines that get comments (owner rule, research-social-growth): people answer a question about a photo of real
 * work. Each one starts a post; the rest is the owner's.
 */
export const HOOK_STARTERS = [
  "Before or after? Tell us which one you'd want to come home to.",
  "Rate this one from 1 to 10.",
  "Wrong answers only. What happened here?",
  "Be honest. How long has yours looked like this?",
  "Tag someone who needs to see this.",
  "Save this for your next weekend.",
] as const;

/** A day and a clock time in a time zone as an ISO moment ("2026-11-03", "10:00", "America/Chicago"). */
export function wallToIso(day: string, time: string, tz: string): string {
  const [y, m, d] = day.split("-").map(Number);
  const [h, mi] = time.split(":").map(Number);
  const want = Date.UTC(y!, m! - 1, d, h, mi);
  const seen = (ms: number) => {
    const p = Object.fromEntries(
      new Intl.DateTimeFormat("en-US", {
        timeZone: tz,
        hourCycle: "h23",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
        .formatToParts(ms)
        .map((x) => [x.type, x.value]),
    );
    return Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour), Number(p.minute));
  };
  // Twice, so a guess on the other side of a clock change lands right.
  let at = want - (seen(want) - want);
  at = at - (seen(at) - want);
  return new Date(at).toISOString();
}

/** An ISO moment as the day and clock time in a time zone: {day: "2026-11-03", time: "10:00"}. */
export function isoToWall(iso: string, tz: string): { day: string; time: string } {
  const at = new Date(iso);
  return {
    day: at.toLocaleDateString("en-CA", { timeZone: tz }),
    time: at.toLocaleTimeString("en-GB", { timeZone: tz, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }),
  };
}

/** The brand's hashtag line, under every post (owner rule): "The Cleaners of New Orleans" gives #TheCleaners #TheTrades. */
export function brandTags(companyName: string): string {
  const brand = companyName.replace(/\s+of\s+.*$/i, "").replace(/[^A-Za-z0-9]/g, "");
  return brand && brand.toLowerCase() !== "thetrades" ? `#${brand} #TheTrades` : "#TheTrades";
}

export interface SocialTip {
  ok: boolean;
  text: string;
}

/**
 * Soft checks shown while writing (they never stop a save): the hook fits before "more", it asks for a comment, a few
 * hashtags at most, the brand line is there.
 */
export function socialPostTips(body: string, platforms: readonly string[], companyName: string): SocialTip[] {
  const t = body.trim();
  const first = t.split(/\n/)[0] ?? "";
  const tags = t.match(/#[\p{L}\p{N}_]+/gu) ?? [];
  const brand = brandTags(companyName).split(" ")[0]!.toLowerCase();
  const out: SocialTip[] = [
    { ok: first.length > 0 && first.length <= SOCIAL_FOLD, text: 'The first line fits before "more"' },
    { ok: /\?|\b(comment|tag|save|share|tell us|drop a|which one|rate)\b/i.test(t), text: "Asks people to comment, tag or save" },
    { ok: t.toLowerCase().includes(brand), text: "Ends with the brand tags" },
  ];
  if (platforms.includes("instagram"))
    out.push({ ok: tags.length <= SOCIAL_MAX_TAGS, text: `${String(SOCIAL_MAX_TAGS)} hashtags at most on Instagram` });
  return out;
}

/** Checks a review reply (gbp.php): 1 to 3 sentences, thanks them, no dashes, colons, emojis, hashtags or discounts. */
export function replyProblems(reply: string): string[] {
  const out: string[] = [];
  const t = reply.trim();
  if (!t) return ["Write a reply."];
  if ((t.match(/[.!?](\s|$)/g) ?? []).length > 3) out.push("Keep it to 3 sentences.");
  if (/[–—:]| - /.test(t)) out.push("No dashes or colons.");
  if (/#\w/.test(t)) out.push("No hashtags.");
  if (/\p{Extended_Pictographic}/u.test(t)) out.push("No emojis.");
  if ((t.match(/!/g) ?? []).length > 1) out.push("One exclamation mark at most.");
  if (/\b(delighted|thrilled|journey)\b/i.test(t)) out.push("Use plain words.");
  if (/\b(discount|% off|coupon|free)\b/i.test(t)) out.push("No discounts in a reply.");
  return out;
}

/** The work a review talks about, first match wins; else the trade's main service. Plain words people search for. */
const REVIEW_SERVICES: [RegExp, string][] = [
  [/move[\s-]?(out|in)/i, "move out cleaning"],
  [/deep[\s-]?clean/i, "deep cleaning"],
  [/air\s?bnb|turnover/i, "Airbnb cleaning"],
  [/office|commercial|janitor/i, "office cleaning"],
  [/soft[\s-]?wash/i, "soft washing"],
  [/roof/i, "roof cleaning"],
  [/gutter/i, "gutter cleaning"],
  [/pressure[\s-]?wash|power[\s-]?wash/i, "pressure washing"],
  [/landscap/i, "landscaping"],
  [/lawn|mow|grass|yard/i, "lawn care"],
];
const TRADE_SERVICE: Record<string, string> = { cleaning: "house cleaning", lawn: "lawn care", washers: "pressure washing" };

/** "1740 Shirley Dr, New Orleans, LA 70114, USA" is New Orleans: the part before the state and ZIP. */
export function cityFromAddress(address: string): string | null {
  const parts = address.split(",").map((s) => s.trim());
  const at = parts.findIndex((s) => /^[A-Z]{2}(\s+\d{5}(-\d{4})?)?$/.test(s));
  return at > 0 ? (parts[at - 1] ?? null) : null;
}

/** +15049009981 as (504) 900-9981, the way people read it. */
const phoneWords = (p: string) => {
  const m = /^\+?1?(\d{3})(\d{3})(\d{4})$/.exec(p.replace(/[^\d+]/g, ""));
  return m ? `(${m[1]!}) ${m[2]!}-${m[3]!}` : p;
};

/**
 * A first reply to edit (the old Steward asked Claude; this is the plain starting draft). Owner, 2026-11-02: replies make
 * us look good and help the profile show up, so they name the service and the city once, the way a person would. A bad
 * review gets sympathy, not a fix pushed on them: if they give us another chance we make it up to them. The wording
 * changes from review to review (picked by the review), so a page of replies never reads like one copied line.
 */
export function replyDraft(r: {
  author: string;
  stars: number;
  brandPhone: string | null;
  text?: string;
  /** The company name, like The Cleaners of New Orleans. */
  brand?: string | null;
  city?: string | null;
  trade?: string | null;
}): string {
  const word = r.author.trim().split(/\s+/)[0] ?? "";
  // A real first name only: not "A Google user" or "Laiah's awesome Chanel Owens".
  const first = /^[A-Z][a-z]+$/.test(word) && r.author.trim() !== "A Google user" ? word : "";
  const text = r.text ?? "";
  const service = REVIEW_SERVICES.find(([re]) => re.test(text))?.[1] ?? TRADE_SERVICE[r.trade ?? ""] ?? TRADE_SERVICE.cleaning!;
  const brand = r.brand?.trim() ? r.brand.trim() : "us";
  const inCity = r.city ? ` in ${r.city}` : "";
  const hi = (s: string) => (first ? `${s}, ${first}.` : `${s}.`);
  const pick = <T>(list: readonly T[]): T => {
    let h = 0;
    for (const ch of `${r.author}|${text}`) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
    return list[h % list.length]!;
  };
  if (r.stars >= 5)
    return pick([
      `${hi("Thank you")} We are so happy you loved your ${service}${inCity}, and we can't wait to see you again.`,
      `${hi("Thanks so much")} It means a lot that you trust ${brand} with your ${service}. We look forward to your next visit.`,
      `${hi("Thank you so much")} Our team takes real pride in ${service}${inCity}, and reviews like yours make their day.`,
      `${hi("Thank you")} We will pass this on to the team. We are glad we could help with your ${service}${inCity}!`,
    ]);
  if (r.stars === 4)
    return pick([
      `${hi("Thank you")} We are glad you were happy with your ${service}${inCity}. We always want to earn that fifth star, and we will keep at it.`,
      `${hi("Thanks for the great review")} It was a pleasure taking care of your ${service}. We hope to see you again soon.`,
    ]);
  if (r.stars === 3)
    return `${hi("Thank you for the honest review")} We are sorry your ${service} was not everything you hoped for. If you give us another chance, we will make it up to you.`;
  if (!text.trim() && r.brandPhone)
    return `${hi("We are sorry")} It sounds like your visit fell short, and we would like to hear what happened. If you are willing, call us at ${phoneWords(r.brandPhone)}.`;
  return pick([
    `${hi("We are truly sorry")} This is not the ${service} we want anyone${inCity} to get from us. If you ever give us another chance, we will make it up to you.`,
    `${hi("We hear you")} We are sorry your ${service} let you down, and we are sharing this with our team. If you decide to give us another try, we will make it right.`,
  ]);
}

/** A reviewer is a client when exactly one client has that full name, or first name and last initial. */
export function reviewerMatches(author: string, clientName: string): boolean {
  // Letters only, no accents or marks ("José O'Neil" is "jose oneil"); a middle name on either side is left out.
  const words = (s: string) =>
    s
      .normalize("NFD")
      .toLowerCase()
      .replace(/[^a-z\s]/g, "")
      .split(/\s+/)
      .filter(Boolean);
  const a = words(author);
  const c = words(clientName);
  if (a.length < 2 || c.length < 2 || a[0] !== c[0]) return false;
  const last = a[a.length - 1]!;
  const theirs = c[c.length - 1]!;
  return last === theirs || (last.length === 1 && theirs.startsWith(last));
}
